/**
 * Contenido con vigencia: qué fichas están por venir, cuáles corriendo y cuáles
 * ya pasaron.
 *
 * ── Por qué esto no puede decidirse al publicar ─────────────────────────────
 * El sitio es estático. Si una lista de «próximas ventas» se filtra en el
 * generador, una publicada el 10 de agosto sigue anunciando como próxima una
 * venta que terminó el 16. Nadie republica el domingo por la noche.
 *
 * Así que el estado se calcula DOS veces, igual que hace el precio de una
 * oferta: se imprime al publicar —para que la página sea correcta sin
 * JavaScript y para que un buscador no vea una página con todo oculto— y se
 * vuelve a calcular en el navegador al cargarla, que es el único momento que
 * sabe qué día es hoy.
 *
 * ── Fecha sola y fecha con hora ─────────────────────────────────────────────
 * Los valores antiguos (`2026-08-16`) siguen siendo días completos. Los nuevos
 * pueden añadir hora local (`2026-08-16T17:30`) y entonces se comparan hasta el
 * minuto. Nunca se interpretan como UTC: son la fecha y hora que escribió la
 * persona en el CMS.
 *
 * ── Genérico a propósito ────────────────────────────────────────────────────
 * No sabe nada de ventas. Una ventana con tres estados sirve igual para
 * eventos, cursos, convocatorias, vacantes o menús del día. Los nombres son
 * neutros; los rótulos que se ven —«SALE ENDED»— son texto del usuario.
 */
export const SCHEDULE_STATES = ['upcoming', 'current', 'past'];
export const SCHEDULE_STATE_LABELS = {
    upcoming: 'Por venir',
    current: 'En curso',
    past: 'Terminado',
};
export function parseDateRange(raw) {
    const value = (raw ?? '').trim();
    if (!value)
        return null;
    if (value.startsWith('{')) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                const record = parsed;
                const start = typeof record.start === 'string' ? record.start.trim() : '';
                if (!start)
                    return null;
                return { start, end: typeof record.end === 'string' ? record.end.trim() : '' };
            }
        }
        catch {
            /* JSON roto: se cae al formato simple en vez de perder el valor. */
        }
    }
    /* `2026-08-16/2026-08-18`, que es lo que se escribe a mano al importar una
       colección desde fuera.
  
       Estricto a propósito: una FECHA SUELTA no es un rango. Aceptarla como tal
       hacía que un campo de fecha normal se leyera como una vigencia con fin
       vacío, y con eso el campo «Fin» de una colección declarada a la antigua
       dejaba de contar — las ventas de varios días salían terminadas el mismo día
       que empezaban. */
    const pair = value.match(/^(\d{4}-\d{2}-\d{2})\s*\/\s*(\d{4}-\d{2}-\d{2})$/);
    return pair ? { start: pair[1], end: pair[2] } : null;
}
export function serializeDateRange(range) {
    if (!range?.start)
        return '';
    return JSON.stringify(range.end ? { start: range.start, end: range.end } : { start: range.start });
}
/** Un día del calendario como número comparable: 2026-08-16 → 20260816. */
function dayNumber(value) {
    const match = (value ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match)
        return null;
    const [, year, month, day] = match;
    const n = Number(year) * 10000 + Number(month) * 100 + Number(day);
    return Number.isFinite(n) ? n : null;
}
/** Una fecha/hora LOCAL como número comparable, sin conversiones de huso. */
function minuteNumber(value, endOfDay = false) {
    const match = (value ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2})?)?$/);
    if (!match)
        return null;
    const [, year, month, day, hour, minute] = match;
    const date = Number(year) * 10000 + Number(month) * 100 + Number(day);
    const clock = hour == null
        ? (endOfDay ? 2359 : 0)
        : Number(hour) * 100 + Number(minute);
    const result = date * 10000 + clock;
    return Number.isFinite(result) ? result : null;
}
function nowMinuteNumber(now) {
    return todayNumber(now) * 10000 + now.getHours() * 100 + now.getMinutes();
}
/** El día de hoy en el huso de quien mira, con el mismo formato. */
export function todayNumber(now = new Date()) {
    return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
}
/**
 * En qué estado está una ficha hoy.
 *
 * Devuelve `null` cuando no se puede decidir —la colección no declara ventana,
 * o la ficha no tiene fecha de inicio—, que es distinto de decidir mal: quien
 * llama deja pasar la ficha en vez de esconderla. Una ficha sin fecha no es una
 * ficha terminada.
 */
export function scheduleStateFor(values, window, now = new Date()) {
    if (!window?.startField || !values)
        return null;
    /* Un campo de vigencia lleva las dos fechas dentro. Se comprueba primero: el
       valor no es una fecha suelta, así que leerlo como tal daría `null` y la
       ficha quedaría sin estado. */
    const range = parseDateRange(values[window.startField]);
    const startRaw = range ? range.start : values[window.startField];
    const endRaw = range
        ? range.end
        : (window.endField ? values[window.endField] : undefined);
    /* En cuanto una punta declara hora, la ventana entera trabaja por minuto.
       Una punta que sólo trae fecha conserva su significado natural: 00:00 al
       empezar y 23:59 al terminar. Sin final, dura hasta acabar ese día. */
    const hasTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(startRaw ?? '')
        || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(endRaw ?? '');
    if (hasTime) {
        const startMinute = minuteNumber(startRaw);
        if (startMinute === null)
            return null;
        const endMinute = minuteNumber(endRaw, true) ?? minuteNumber(startRaw, true) ?? startMinute;
        const lastMinute = Math.max(startMinute, endMinute);
        const currentMinute = now instanceof Date ? nowMinuteNumber(now) : now * 10000 + 1200;
        if (currentMinute < startMinute)
            return 'upcoming';
        if (currentMinute > lastMinute)
            return 'past';
        return 'current';
    }
    const start = dayNumber(startRaw);
    if (start === null)
        return null;
    /* Sin fecha de fin, la ventana dura ese día entero. Terminar al empezar
       dejaría cualquier cosa de un solo día en «terminada» el mismo día que
       ocurre, que es justo cuando más importa que se vea. */
    const end = dayNumber(endRaw) ?? start;
    /* Fin antes que inicio es un error de captura, no un estado: se toma la
       ventana como el día de inicio en vez de dejar la ficha en un limbo que no
       sale en ninguna pestaña y nadie sabe por qué. */
    const last = Math.max(start, end);
    const today = now instanceof Date ? todayNumber(now) : now;
    if (today < start)
        return 'upcoming';
    if (today > last)
        return 'past';
    return 'current';
}
/**
 * Si una ficha entra en un filtro de estados.
 *
 * Sin estado —ficha sin fechas— entra siempre: el filtro dice qué quitar, y
 * quitar lo que no se puede clasificar esconde contenido sin explicación.
 */
export function matchesScheduleFilter(state, wanted) {
    if (!wanted || wanted.length === 0)
        return true;
    if (state === null)
        return true;
    return wanted.includes(state);
}
/** Lee un filtro guardado como texto (`"upcoming,current"`) a lista de estados. */
export function parseScheduleFilter(value) {
    if (!value)
        return [];
    return value.split(',')
        .map((part) => part.trim())
        .filter((part) => SCHEDULE_STATES.includes(part));
}
/**
 * La vigencia de una colección, sin tener que declararla.
 *
 * Con un campo de tipo «Vigencia», la colección YA dice cuál es: buscarlo es
 * todo lo que hace falta, y el paso de ir a los ajustes a emparejar dos fechas
 * desaparece. La declaración explícita sigue mandando cuando existe — es como se
 * configuraron las colecciones anteriores a este tipo de campo, y quitársela
 * rompería sus listas en silencio.
 */
export function collectionScheduleWindow(collection) {
    /**
     * El campo de Vigencia MANDA sobre cualquier otra cosa.
     *
     * Antes ganaba la declaración de los ajustes, y eso dejaba una trampa: quien
     * ya la tenía apuntando a dos campos de fecha y luego añadía un campo de
     * Vigencia veía cómo su campo nuevo no hacía nada. Si las viejas fechas
     * estaban vacías, las fichas se quedaban SIN estado — y sin estado, un
     * elemento con «mostrar sólo mientras ocurre» no se esconde nunca, porque no
     * hay nada contra lo que decidir. Se veía como que la función no funcionaba.
     *
     * Añadir un campo de Vigencia es la declaración más clara que se puede hacer,
     * así que gana. La de los ajustes queda de reserva para las colecciones
     * anteriores a que ese tipo de campo existiera.
     */
    const range = collection?.fields?.find((field) => field.type === 'dateRange');
    if (range)
        return { startField: range.id };
    return collection?.schedule?.startField ? collection.schedule : undefined;
}
//# sourceMappingURL=cmsSchedule.js.map